import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * FindingSoftwareChapter — Formula → Music Player Transformation (200vh pinned)
 *
 * Scroll drives a scene that transforms:
 * Physical experimentation → calculation → software interface
 *
 * 0–25%:  Cricket chirp formula appears with animated number
 * 25–50%: Code lines appear character by character
 * 50–80%: Spotify-clone music player assembles (album art, controls, waveform)
 * 80–100%: One phrase: "THIS WAS THE FIRST TIME SOFTWARE FELT LIKE BUILDING."
 */
const FindingSoftwareChapter = () => {
  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const formulaRef = useRef(null);
  const codeRef = useRef(null);
  const playerRef = useRef(null);
  const playerArtRef = useRef(null);
  const playerControlsRef = useRef(null);
  const playerWaveRef = useRef(null);
  const closingRef = useRef(null);
  const annotationRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const setup = () => {
      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: '+=280%',
            pin: sceneRef.current,
            scrub: 1.0,
            anticipatePin: 1,
          }
        });

        // 0–20%: annotation + formula appears
        tl.fromTo(annotationRef.current,
          { opacity: 0, y: -10 },
          { opacity: 1, y: 0, ease: 'power2.out', duration: 0.4 },
          0
        )
        .fromTo(formulaRef.current,
          { opacity: 0, y: 30, scale: 0.95 },
          { opacity: 1, y: 0, scale: 1, ease: 'power3.out', duration: 0.8 },
          0.2
        )

        // 20–40%: formula fades out completely, code panel enters
        .to(formulaRef.current, { opacity: 0, scale: 0.92, duration: 0.4 }, 1.2)
        .fromTo(codeRef.current,
          { opacity: 0, x: 35 },
          { opacity: 1, x: 0, ease: 'power3.out', duration: 0.7 },
          1.3
        )

        // 40–70%: code fades out completely, music player assembles & holds
        .to(codeRef.current, { opacity: 0, duration: 0.4 }, 2.2)
        .fromTo(playerRef.current,
          { opacity: 0, scale: 0.88, y: 30 },
          { opacity: 1, scale: 1, y: 0, ease: 'back.out(1.4)', duration: 0.7 },
          2.4
        )
        .fromTo(playerArtRef.current,
          { scale: 0, rotate: -15 },
          { scale: 1, rotate: 0, ease: 'back.out(1.7)', duration: 0.5 },
          2.6
        )
        .fromTo(playerControlsRef.current.children,
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, stagger: 0.06, ease: 'power2.out', duration: 0.35 },
          2.9
        )
        .fromTo(playerWaveRef.current.children,
          { scaleY: 0.1 },
          { scaleY: 1, stagger: 0.03, ease: 'elastic.out(1, 0.5)', duration: 0.5 },
          3.1
        )

        // 70–100%: Player cleanly fades OUT completely (no overlap)
        .to(playerRef.current, {
          opacity: 0,
          scale: 0.9,
          y: -20,
          duration: 0.5,
          ease: 'power2.in'
        }, 4.0)

        // Closing phrase reveals in clean, empty center stage with hold
        .fromTo(closingRef.current,
          { opacity: 0, scale: 0.92, y: 25 },
          { opacity: 1, scale: 1, y: 0, ease: 'power3.out', duration: 0.7 },
          4.4
        )
        // Dedicated reading hold buffer so it is never covered or rushed
        .to({}, { duration: 1.2 }, 5.1)
        // Seamless exit
        .to(closingRef.current, {
          opacity: 0,
          y: -15,
          duration: 0.5,
          ease: 'power2.in'
        }, 6.3);
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  // Generate waveform bars
  const waveBars = Array.from({ length: 28 }, (_, i) => {
    const heights = [20, 35, 55, 70, 80, 75, 60, 45, 65, 85, 70, 50, 40, 60, 75, 90, 80, 65, 55, 70, 60, 45, 35, 50, 65, 75, 55, 30];
    return heights[i % heights.length];
  });

  return (
    <section ref={wrapperRef} className="fsw-scroll-wrapper">
      <div ref={sceneRef} className="fsw-scene">

        {/* Background */}
        <div className="fsw-bg" aria-hidden="true" />

        {/* Annotation */}
        <div ref={annotationRef} className="fsw-annotation font-mono">
          CH. 03 &nbsp;// FINDING SOFTWARE
        </div>

        {/* Phase 1: Cricket Chirp Formula */}
        <div ref={formulaRef} className="fsw-formula">
          <div className="fsw-formula-tag font-mono">INSECT CHIRP TEMPERATURE CALCULATOR</div>
          <div className="fsw-formula-body">
            <div className="fsw-formula-row">
              <span className="fsw-var font-mono">CHIRPS</span>
              <span className="fsw-op">/</span>
              <span className="fsw-val font-mono">14sec</span>
              <span className="fsw-op">+</span>
              <span className="fsw-val font-mono">40</span>
            </div>
            <div className="fsw-arrow">↓</div>
            <div className="fsw-result">
              <span className="fsw-result-label font-mono">TEMPERATURE (°F)</span>
              <span className="fsw-result-value font-mono">72°</span>
            </div>
          </div>
          <div className="fsw-caption font-mono">MY FIRST ANDROID APP — JAVA, 2023</div>
        </div>

        {/* Phase 2: Code panel */}
        <div ref={codeRef} className="fsw-code-panel font-mono">
          <div className="fsw-code-header">
            <span className="fsw-dot red" />
            <span className="fsw-dot yellow" />
            <span className="fsw-dot green" />
            <span className="fsw-code-filename">TemperatureCalculator.java</span>
          </div>
          <div className="fsw-code-body">
            <div className="fsw-line"><span className="fsw-kw">int</span> chirps = <span className="fsw-num">14</span>;</div>
            <div className="fsw-line"><span className="fsw-kw">double</span> temp = (chirps + <span className="fsw-num">40</span>);</div>
            <div className="fsw-line fsw-comment">{'// Dolbear\'s Law'}</div>
            <div className="fsw-line"><span className="fsw-kw">return</span> <span className="fsw-str">"Temperature: "</span> + temp + <span className="fsw-str">"°F"</span>;</div>
            <div className="fsw-line fsw-blank" />
            <div className="fsw-line fsw-comment">{'// Then came the Spotify clone...'}</div>
          </div>
        </div>

        {/* Phase 3: Music player */}
        <div ref={playerRef} className="fsw-player">
          {/* Album art */}
          <div ref={playerArtRef} className="fsw-player-art">
            <svg viewBox="0 0 80 80" width="80" height="80">
              <circle cx="40" cy="40" r="38" fill="rgba(212,163,89,0.12)" stroke="rgba(212,163,89,0.5)" strokeWidth="1.5" />
              <circle cx="40" cy="40" r="22" fill="rgba(212,163,89,0.08)" stroke="rgba(212,163,89,0.3)" strokeWidth="1" />
              <circle cx="40" cy="40" r="5" fill="rgba(212,163,89,0.6)" />
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <line key={i}
                  x1="40" y1="18" x2="40" y2="24"
                  stroke="rgba(212,163,89,0.35)"
                  strokeWidth="1.5"
                  transform={`rotate(${deg} 40 40)`}
                />
              ))}
            </svg>
          </div>

          <div className="fsw-player-info">
            <div className="fsw-player-track font-display">Spotify Clone</div>
            <div className="fsw-player-artist font-mono">SHAWAIZ ALI &nbsp;•&nbsp; ANDROID JAVA</div>
          </div>

          {/* Controls */}
          <div ref={playerControlsRef} className="fsw-player-controls">
            <button className="fsw-ctrl fsw-ctrl-skip" aria-label="Previous">⏮</button>
            <button className="fsw-ctrl fsw-ctrl-play" aria-label="Play">▶</button>
            <button className="fsw-ctrl fsw-ctrl-skip" aria-label="Next">⏭</button>
          </div>

          {/* Progress bar */}
          <div className="fsw-player-progress">
            <div className="fsw-progress-track">
              <div className="fsw-progress-fill" />
            </div>
            <div className="fsw-progress-times font-mono">
              <span>1:23</span><span>3:47</span>
            </div>
          </div>

          {/* Waveform */}
          <div ref={playerWaveRef} className="fsw-waveform" aria-hidden="true">
            {waveBars.map((h, i) => (
              <div
                key={i}
                className="fsw-wave-bar"
                style={{
                  height: `${h}%`,
                  opacity: i < 11 ? 1 : 0.3,
                  background: i < 11 ? 'rgba(212,163,89,0.8)' : 'rgba(107,114,128,0.4)'
                }}
              />
            ))}
          </div>

          {/* Labels */}
          <div className="fsw-player-labels font-mono">
            <span className="fsw-label-item">ExoPlayer</span>
            <span className="fsw-label-item">Media3</span>
            <span className="fsw-label-item">SQLite</span>
            <span className="fsw-label-item">Firebase</span>
          </div>
        </div>

        {/* Closing */}
        <div ref={closingRef} className="fsw-closing">
          <div className="fsw-closing-text font-display">
            THIS WAS THE FIRST TIME<br />
            <span className="fsw-closing-accent">SOFTWARE FELT LIKE BUILDING.</span>
          </div>
          <div className="fsw-closing-sub font-mono">CHAPTER 03 COMPLETE</div>
        </div>
      </div>

      <style>{`
        .fsw-scroll-wrapper {
          position: relative;
          height: 300vh;
          background: #0e121b;
        }

        .fsw-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #0e121b;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform;
        }

        .fsw-bg {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 163, 89, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 163, 89, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          pointer-events: none;
        }

        .fsw-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.5);
          will-change: opacity, transform;
        }

        /* Formula */
        .fsw-formula {
          position: absolute;
          text-align: center;
          will-change: opacity, transform;
        }

        .fsw-formula-tag {
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          color: rgba(56, 189, 248, 0.7);
          margin-bottom: 1.5rem;
        }

        .fsw-formula-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .fsw-formula-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .fsw-var {
          font-size: clamp(1rem, 2vw, 1.4rem);
          color: rgba(212, 163, 89, 0.9);
          font-weight: 700;
        }

        .fsw-op {
          font-size: 1.5rem;
          color: rgba(107, 114, 128, 0.6);
        }

        .fsw-val {
          font-size: clamp(1rem, 2vw, 1.4rem);
          color: rgba(56, 189, 248, 0.8);
        }

        .fsw-arrow {
          font-size: 1.5rem;
          color: rgba(107, 114, 128, 0.5);
        }

        .fsw-result {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .fsw-result-label {
          font-size: 0.7rem;
          letter-spacing: 0.16em;
          color: rgba(107, 114, 128, 0.7);
        }

        .fsw-result-value {
          font-size: clamp(2rem, 5vw, 4rem);
          font-weight: 700;
          color: #10b981;
        }

        .fsw-caption {
          margin-top: 1.5rem;
          font-size: 0.65rem;
          letter-spacing: 0.15em;
          color: rgba(107, 114, 128, 0.5);
        }

        /* Code panel */
        .fsw-code-panel {
          position: absolute;
          background: #0b0e14;
          border: 1px solid rgba(56, 189, 248, 0.15);
          border-radius: 10px;
          width: min(440px, 88vw);
          will-change: opacity, transform;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
        }

        .fsw-code-header {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .fsw-dot {
          width: 10px; height: 10px;
          border-radius: 50%;
        }

        .fsw-dot.red { background: #ff5f56; }
        .fsw-dot.yellow { background: #ffbd2e; }
        .fsw-dot.green { background: #27c93f; }

        .fsw-code-filename {
          margin-left: auto;
          font-size: 0.7rem;
          color: rgba(107, 114, 128, 0.7);
        }

        .fsw-code-body {
          padding: 1rem 1.25rem;
          font-size: 0.82rem;
          line-height: 1.8;
        }

        .fsw-line { color: #9ca3af; }
        .fsw-kw { color: #38bdf8; }
        .fsw-num { color: #fbbf24; }
        .fsw-str { color: #34d399; }
        .fsw-comment { color: rgba(107,114,128,0.6); font-style: italic; }
        .fsw-blank { height: 0.5rem; }

        /* Player */
        .fsw-player {
          position: absolute;
          background: #0b0e14;
          border: 1px solid rgba(212, 163, 89, 0.2);
          border-radius: 16px;
          padding: 1.75rem 2rem;
          width: min(400px, 88vw);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          will-change: opacity, transform;
          box-shadow: 0 0 60px rgba(212, 163, 89, 0.06);
        }

        .fsw-player-art {
          margin: 0 auto;
          will-change: transform, opacity;
        }

        .fsw-player-info {
          text-align: center;
        }

        .fsw-player-track {
          font-size: 1.3rem;
          font-weight: 700;
          color: #f3f4f6;
        }

        .fsw-player-artist {
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          color: rgba(107, 114, 128, 0.8);
          margin-top: 0.25rem;
        }

        .fsw-player-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }

        .fsw-ctrl {
          background: none;
          border: none;
          cursor: pointer;
          color: rgba(212, 163, 89, 0.7);
          font-size: 1rem;
          transition: color 0.2s;
        }

        .fsw-ctrl-play {
          width: 44px; height: 44px;
          border-radius: 50%;
          background: rgba(212, 163, 89, 0.12);
          border: 1px solid rgba(212, 163, 89, 0.3) !important;
          font-size: 1.1rem;
        }

        .fsw-player-progress {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .fsw-progress-track {
          width: 100%;
          height: 3px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 3px;
          overflow: hidden;
        }

        .fsw-progress-fill {
          width: 37%;
          height: 100%;
          background: rgba(212, 163, 89, 0.8);
          border-radius: 3px;
        }

        .fsw-progress-times {
          display: flex;
          justify-content: space-between;
          font-size: 0.65rem;
          color: rgba(107, 114, 128, 0.7);
        }

        .fsw-waveform {
          display: flex;
          align-items: flex-end;
          gap: 2px;
          height: 40px;
        }

        .fsw-wave-bar {
          flex: 1;
          border-radius: 1px;
          will-change: transform;
          transform-origin: bottom;
        }

        .fsw-player-labels {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .fsw-label-item {
          font-size: 0.62rem;
          letter-spacing: 0.1em;
          color: rgba(56, 189, 248, 0.6);
          background: rgba(56, 189, 248, 0.05);
          border: 1px solid rgba(56, 189, 248, 0.15);
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
        }

        /* Closing */
        .fsw-closing {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 3rem;
          will-change: opacity, transform;
        }

        .fsw-closing-text {
          font-size: clamp(1.5rem, 3.5vw, 2.8rem);
          font-weight: 800;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: rgba(243, 244, 246, 0.9);
          margin-bottom: 1.5rem;
        }

        .fsw-closing-accent {
          color: var(--accent-gold);
        }

        .fsw-closing-sub {
          font-size: 0.65rem;
          letter-spacing: 0.2em;
          color: rgba(107, 114, 128, 0.5);
        }

        @media (max-width: 768px) {
          .fsw-scroll-wrapper {
            height: 320vh;
          }

          /* Annotation text safely below mobile topbar */
          .fsw-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          /* Player scaled for mobile viewport */
          .fsw-player {
            width: min(370px, 90vw);
            padding: 1.25rem 1.4rem;
            gap: 0.75rem;
          }

          .fsw-player-art svg {
            width: 64px;
            height: 64px;
          }

          .fsw-player-track {
            font-size: 1.1rem;
          }

          .fsw-waveform {
            height: 32px;
          }

          /* Code block */
          .fsw-code-panel {
            width: min(380px, 90vw);
          }

          .fsw-code-body {
            padding: 0.85rem 1rem;
            font-size: 0.75rem;
            line-height: 1.65;
          }

          /* Closing text — clean and centered */
          .fsw-closing {
            padding: 1.5rem;
          }

          .fsw-closing-text {
            font-size: clamp(1.25rem, 6vw, 2.1rem);
            white-space: normal;
            text-align: center;
            line-height: 1.3;
            width: 90vw;
            margin-bottom: 1rem;
          }

          .fsw-closing-sub {
            font-size: 0.58rem;
            letter-spacing: 0.16em;
          }
        }
      `}</style>
    </section>
  );
};

export default FindingSoftwareChapter;
